import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const ITEM_H = 44;
const VISIBLE = 5;
const PAD = (ITEM_H * (VISIBLE - 1)) / 2;

export function WheelColumn({ options, value, onChange, className = "" }) {
  const ref = useRef(null);
  const timer = useRef(null);
  const settling = useRef(false);

  useEffect(() => {
    const idx = Math.max(0, options.findIndex((o) => o.value === value));
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ITEM_H) !== idx) {
      settling.current = true;
      el.scrollTop = idx * ITEM_H;
      const t = setTimeout(() => { settling.current = false; }, 150);
      return () => clearTimeout(t);
    }
  }, [value, options]);

  const onScroll = () => {
    if (settling.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const i = Math.min(options.length - 1, Math.max(0, Math.round(el.scrollTop / ITEM_H)));
      if (options[i] && options[i].value !== value) onChange(options[i].value);
    }, 110);
  };

  return (
    <div ref={ref} onScroll={onScroll}
      className={`overflow-y-auto snap-y snap-mandatory no-scrollbar ${className}`}
      style={{ height: ITEM_H * VISIBLE, paddingTop: PAD, paddingBottom: PAD }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button key={String(o.value)} type="button" onClick={() => onChange(o.value)}
            data-testid={`wheel-option-${String(o.value).replace(/[^a-zA-Z0-9]+/g, "-")}`}
            className={`w-full snap-center flex items-center justify-center font-heading transition-all duration-150 ${
              active ? "text-foreground text-xl font-bold" : "text-muted-foreground/50 text-lg"}`}
            style={{ height: ITEM_H }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function WheelSheet({ open, title, onCancel, onDone, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80]" data-testid="wheel-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="absolute bottom-0 left-0 right-0 bg-card border-t rounded-t-2xl shadow-2xl" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="flex items-center justify-between px-5 h-14 border-b">
          <button type="button" onClick={onCancel} data-testid="wheel-cancel"
            className="text-[var(--cc-accent)] font-medium text-base">Cancel</button>
          <p className="font-heading font-bold text-base">{title}</p>
          <button type="button" onClick={onDone} data-testid="wheel-done"
            className="text-[var(--cc-accent)] font-bold text-base">Done</button>
        </div>
        <div className="relative max-w-lg mx-auto">
          <div className="pointer-events-none absolute left-4 right-4 top-1/2 -translate-y-1/2 rounded-xl bg-secondary/80" style={{ height: 44 }} />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-[hsl(var(--card))] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[hsl(var(--card))] to-transparent z-10" />
          <div className="relative flex px-4">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
